// What every holdings view uses to present a managed model: the expand control, the
// "Model" tag, and the expanded-set hook. Shared so By Fund, By Source, By Asset Class
// and Performance all draw and operate a model the same way.
import { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { cx } from '../../../../utils/cx';
import './model-row.css';

/**
 * Which rows are expanded — the one toggle-set pattern the holdings views use for
 * models (and By Source for its sources). `openKeys`/`setOpenKeys` serve "expand all".
 */
export function useModelExpand() {
  const [openKeys, setOpenKeys] = useState(() => new Set());
  const toggle = useCallback((key) => {
    setOpenKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);
  const isOpen = useCallback((key) => openKeys.has(key), [openKeys]);
  return { isOpen, toggle, openKeys, setOpenKeys };
}

// Expand / collapse control on a model row: a plus that becomes a minus. A real
// button with aria-expanded, so the group is operable and announced.
export function ModelToggle({ open, name, onToggle, t }) {
  return (
    <button
      type="button"
      className={cx('holdings-model__chev', open && 'is-open')}
      aria-expanded={open}
      aria-label={t(open ? 'portfolio.byFund.collapseModel' : 'portfolio.byFund.expandModel', { name })}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round" aria-hidden="true">
        <line x1="3" y1="7" x2="11" y2="7" />
        {!open && <line x1="7" y1="3" x2="7" y2="11" />}
      </svg>
    </button>
  );
}
ModelToggle.propTypes = {
  open: PropTypes.bool,
  name: PropTypes.string,
  onToggle: PropTypes.func,
  t: PropTypes.func.isRequired,
};

export function ModelTag({ t }) {
  return <span className="holdings-model__tag">{t('portfolio.byFund.model')}</span>;
}
ModelTag.propTypes = { t: PropTypes.func.isRequired };
